import ChartContainer from "@/components/chart-container/ChartContainer";
import DivSkeleton from "@/components/skeletons/DivSkeleton";

import { parametersContants } from "../../constants/parameters-contant";
import { useMemo, useState } from "@wordpress/element";
import { IParameter, IParameters } from "../../types/parametres-type";
import {
	calcMaxValueChart,
	filterMarineForecastByDate,
	findParamsSelected,
	formatParameterName,
} from "../../helpers/forecast-chart-helper";

import { useTranslation } from "react-i18next";
import { useSelect } from "@wordpress/data";
import { WeatherSelect } from "@/store/type";
import { WEATHER_STORE } from "@/store/store";

import "./style.scss";

export const ChartContent = () => {
	const { t } = useTranslation();
	const [parametersData, setParametersData] =
		useState<IParameters[]>(parametersContants);

	const { isLoading, marineData } = useSelect(
		(select: (store: typeof WEATHER_STORE) => WeatherSelect) => {
			const store = select(WEATHER_STORE);

			return {
				...store.getMarineForecastState(),
			};
		},
	);

	const formattedData = useMemo(() => {
		return filterMarineForecastByDate(marineData);
	}, [marineData]);

	const findSelectionnedFirstParams =
		findParamsSelected(parametersData[0].parameters) ??
		parametersData[0].parameters[0];

	const findSelectionnedSecondParams = findParamsSelected(
		parametersData[1].parameters,
	);

	const handleSelectParameter = ({
		parameter,
		idParameters,
	}: {
		parameter: IParameter;
		idParameters: "Parameter_1" | "Parameter_2";
	}) => {
		const [, selectedKey] = parameter.id.split("_");

		const previousSelectedKey = parametersData
			.find((g) => g.id === idParameters)
			?.parameters.find((p) => p.isSelected)
			?.id.split("_")[1];

		const isAlreadySelected = parameter.isSelected;
		const oppositeGroupId =
			idParameters === "Parameter_1" ? "Parameter_2" : "Parameter_1";

		setParametersData((prev) =>
			prev.map((group) => {
				const getSelectParamOne = prev[0].parameters.filter(
					(item) => item.isSelected === true && item.key !== "wind_speed_10m",
				);

				if (
					idParameters === "Parameter_2" &&
					parameter.key === "wind_speed_10m" &&
					getSelectParamOne.length === 0
				) {
					if (group.id === "Parameter_1") {
						return {
							...group,
							parameters: group.parameters.map((p, index) => ({
								...p,
								isSelected: index === 0,
							})),
						};
					}

					if (group.id === "Parameter_2") {
						return {
							...group,
							parameters: group.parameters.map((p) => {
								return {
									...p,
									isSelected: false,
								};
							}),
						};
					}
				}

				if (group.id === idParameters) {
					return {
						...group,
						parameters: group.parameters.map((p) => {
							if (p.id !== parameter.id) {
								return { ...p, isSelected: false };
							}

							return {
								...p,
								isSelected: !isAlreadySelected,
							};
						}),
					};
				}

				if (group.id === oppositeGroupId) {
					return {
						...group,
						parameters: group.parameters.map((p) => {
							const [, key] = p.id.split("_");

							if (key === previousSelectedKey) {
								return {
									...p,
									isDisabled: false,
								};
							}

							if (!isAlreadySelected && key === selectedKey) {
								return {
									...p,
									isDisabled: true,
									isSelected: false,
								};
							}

							return p;
						}),
					};
				}

				return group;
			}),
		);
	};

	return (
		<div className="chart-content">
			<div className="chart-container-wrapper">
				{isLoading ? (
					<DivSkeleton />
				) : (
					<ChartContainer
						data={formattedData}
						firstParamsSelected={findSelectionnedFirstParams}
						secondParamsSelected={findSelectionnedSecondParams}
						maxFirstParams={calcMaxValueChart({
							data: formattedData,
							key: findSelectionnedFirstParams.key,
						})}
						maxSecondParams={calcMaxValueChart({
							data: formattedData,
							key: findSelectionnedSecondParams?.key,
						})}
					/>
				)}
			</div>

			<div className="parameters-container">
				{parametersData.map((paramterLine, idx) => (
					<div className="parameters-row" key={idx}>
						<div className="parameters-label">
							<span
								style={{
									background:
										idx === 0
											? findSelectionnedFirstParams.color
											: findSelectionnedSecondParams?.color,
								}}
							></span>
							<p>{formatParameterName(paramterLine.id)}</p>
						</div>

						<div className="parameters-content">
							{paramterLine.parameters.map((parameter) => (
								<div
									key={parameter.id}
									className={`${
										parameter.isDisabled ? "disabled" : "active"
									} parameters-radio`}
								>
									<input
										disabled={parameter.isDisabled}
										type="radio"
										id={parameter.id}
										name={t(parameter.name)}
										value={parameter.id}
										checked={parameter.isSelected}
										onClick={() =>
											handleSelectParameter({
												parameter,
												idParameters: paramterLine.id,
											})
										}
									/>
									<label htmlFor={parameter.id}>{t(parameter.name)}</label>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default ChartContent;
